import { isEqual, isPlainObject, omitBy, pick } from 'lodash';

import { getTranslation } from '@/composables/data-display-utils';
import { Constants } from '@/constants';
import { themesOfferedToAll } from '@/helpers/theme.helper';
import { i18n } from '@/plugins/i18n';
import type { Locales, PageDto, ThemeManifestEntry } from '@/services/types/api.types';

const t = i18n.global.t;

const { Status } = Constants.Page;

// What a page save may send: the columns the API writes, `PAGE_WRITABLE_COLUMNS` in its pagesService.
export const PAGE_WRITABLE_FIELDS = ['title', 'slug', 'status', 'theme_key', 'layout_key', 'language_enabled'] as const;

export type PageWritableFields = Partial<Pick<PageDto, (typeof PAGE_WRITABLE_FIELDS)[number]>>;

type PageRef = Pick<PageDto, 'id' | 'slug' | 'status'>;

/** Whether an action applies to a page and, when it does not, why, in the administrator's language. */
export interface PageActionState {
  allowed: boolean;
  reason?: string;
}

const statusKeys: Record<number, string> = {
  [Status.Draft]: 'draft',
  [Status.Published]: 'published',
};

/** A page's status as a translated label; an unknown value as itself. */
export const getPageStatusLabel = (status: number) => {
  const key = statusKeys[status];
  return key ? t(`page.status.${key}`) : `${status ?? ''}`;
};

/** The community's homepage is `options.pages.homepage_id`: a value in a JSON column, with no foreign key. */
export const isHomepage = (page: Pick<PageDto, 'id'>, homepageId: number | null | undefined) =>
  homepageId !== null && homepageId !== undefined && Number(page.id) === Number(homepageId);

/**
 * The path the site serves a page at, without its leading slash: '' for the homepage, which is served at `/` whatever
 * its slug, the slug for any other page, and null for a page with neither, which no address reaches.
 */
export const pagePath = (page: Pick<PageDto, 'id' | 'slug'>, homepageId: number | null | undefined): string | null => {
  if (isHomepage(page, homepageId)) return '';
  return page.slug || null;
};

/** Only a published page can be the homepage, as the API's `set-homepage` requires. */
export const canBeHomepage = (page: PageRef, homepageId: number | null | undefined): PageActionState => {
  if (isHomepage(page, homepageId)) return { allowed: false, reason: t('page.actions.already-homepage') };
  if (page.status !== Status.Published) return { allowed: false, reason: t('page.actions.draft-cannot-be-homepage') };
  return { allowed: true };
};

/** The homepage cannot be deleted: `/` would serve nothing. */
export const canBeDeleted = (page: PageRef, homepageId: number | null | undefined): PageActionState =>
  isHomepage(page, homepageId)
    ? { allowed: false, reason: t('page.actions.homepage-cannot-be-deleted') }
    : { allowed: true };

/** A preview opens the page's address on the site, so a page with none cannot be previewed. */
export const canBePreviewed = (page: PageRef, homepageId: number | null | undefined): PageActionState =>
  pagePath(page, homepageId) === null ? { allowed: false, reason: t('page.actions.no-address') } : { allowed: true };

const isBlank = (value: unknown) => value === undefined || value === '';

// The form shows an empty field for null, and leaves out a value it never touched: all three mean "none". A localized
// field holds an entry for every locale the form knows, empty for a language the stored value has none in.
const normalized = (value: unknown) => {
  if (isBlank(value)) return null;
  return isPlainObject(value) ? omitBy(value as object, isBlank) : value;
};

/**
 * The writable fields whose value differs from the saved page. A slug is never sent empty: a page with no slug, such
 * as a migrated homepage, keeps none, and the API refuses a null one. An unchanged slug is never resent either, since
 * the API checks it again against the reserved slugs, which may have grown since it was saved.
 */
export const changedPageFields = (saved: PageWritableFields, values: PageWritableFields): PageWritableFields => {
  const changed = pick(values, PAGE_WRITABLE_FIELDS);
  for (const field of PAGE_WRITABLE_FIELDS) {
    if (!(field in changed) || isEqual(normalized(changed[field]), normalized(saved[field]))) {
      delete changed[field];
    }
  }
  if (normalized(changed.slug) === null) {
    delete changed.slug;
  }
  return changed;
};

/**
 * Whether a save changes an address visitors may already have: the saved page is published, it has a slug, and the
 * save gives it another one. A draft, or a page getting its first slug, has no shared links to break.
 */
export const slugChangeNeedsWarning = (
  saved: Pick<PageDto, 'slug' | 'status'>,
  values: Pick<PageWritableFields, 'slug'>,
) => saved.status === Status.Published && !!saved.slug && !!values.slug && values.slug !== saved.slug;

/**
 * A stored theme that not every community may pick: a custom one, set by Cocoriko in the database, or one the
 * manifest no longer knows. The admin could not set it back once changed, so its field is not editable.
 */
export const isThemeLocked = (themes: ThemeManifestEntry[], storedKey: string | null | undefined) =>
  !!storedKey && !themesOfferedToAll(themes).some((theme) => theme.key === storedKey);

/** The theme a page is drawn with: the one it stores or, when it stores none, the community's default page theme. */
export const effectiveTheme = (
  themes: ThemeManifestEntry[],
  storedKey: string | null | undefined,
  communityDefault: string | null | undefined,
): ThemeManifestEntry | undefined => themes.find((theme) => theme.key === (storedKey || communityDefault));

/**
 * The layouts a page drawn with this theme may pick: the theme's default, a null `layout_key`, then the named ones it
 * declares. None when the theme declares no named layout, or is unknown: the selector is then hidden.
 */
export const layoutOptions = (theme: ThemeManifestEntry | undefined, languageEnabled: Locales[] | undefined) => {
  const layouts = theme?.hosts.page?.layouts ?? [];
  if (!layouts.length) return [];
  return [
    { label: t('page.layout.default'), value: null },
    ...layouts.map((layout) => ({
      label: getTranslation(layout, 'label', languageEnabled) || layout.key,
      value: layout.key,
    })),
  ];
};

/** The layout a page keeps when its theme changes: its own when the new theme offers it, else the theme's default. */
export const layoutAfterThemeChange = (theme: ThemeManifestEntry | undefined, layoutKey: string | null) => {
  if (layoutKey === null) return null;
  return theme?.hosts.page?.layouts?.some((layout) => layout.key === layoutKey) ? layoutKey : null;
};
